import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const ValidationSummary = ({ result }) => {
  const { t } = useTranslation();
  const {
    successful = 0,
    failed = 0,
    errors = [],
    inserted,
    updated,
    skippedDuplicates = 0,
  } = result || {};
  const total = successful + failed;
  const hasBreakdown = typeof inserted === 'number' && typeof updated === 'number';
  const validationErrors = errors?.filter((e) => e?.field !== 'Duplicate') || [];
  const duplicateErrors = errors?.filter((e) => e?.field === 'Duplicate') || [];

  const formatErrorField = (field) => {
    const key = `import.errorFields.${field}`;
    return t(key, { defaultValue: field });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="text-xl font-semibold text-foreground mb-4">{t('import.summary.title')}</h3>

      {/* Summary Stats */}
      <div className={`grid ${hasBreakdown ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-4 mb-6`}>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="FileText" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{t('import.summary.totalProcessed')}</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{total}</p>
        </div>

        {hasBreakdown ? (
          <>
            <div className="bg-success/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="UserPlus" size={16} className="text-success" />
                <span className="text-sm text-success">{t('import.summary.added')}</span>
              </div>
              <p className="text-2xl font-bold text-success">{inserted}</p>
            </div>
            <div className="bg-accent/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="RefreshCw" size={16} className="text-accent" />
                <span className="text-sm text-accent">{t('import.summary.updated')}</span>
              </div>
              <p className="text-2xl font-bold text-accent">{updated}</p>
            </div>
          </>
        ) : (
          <div className="bg-success/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="CheckCircle2" size={16} className="text-success" />
              <span className="text-sm text-success">{t('import.summary.successfullyImported')}</span>
            </div>
            <p className="text-2xl font-bold text-success">{successful}</p>
          </div>
        )}

        <div className="bg-destructive/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="XCircle" size={16} className="text-destructive" />
            <span className="text-sm text-destructive">{t('import.summary.failed')}</span>
          </div>
          <p className="text-2xl font-bold text-destructive">{failed}</p>
        </div>
      </div>

      {skippedDuplicates > 0 && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2 text-sm text-foreground">
            <Icon name="Copy" size={18} className="text-warning flex-shrink-0 mt-0.5" />
            <p>
              {t('import.summary.duplicatesSkipped', { count: skippedDuplicates })}
            </p>
          </div>
        </div>
      )}

      {/* Error Details */}
      {validationErrors?.length > 0 && (
        <div className="border-t border-border pt-4">
          <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="AlertCircle" size={18} className="text-destructive" />
            {t('import.summary.validationErrors')}
          </h4>
          <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
            {validationErrors?.map((error, index) => (
              <div
                key={index}
                className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 text-sm"
              >
                <div className="flex items-start gap-2">
                  <Icon name="AlertTriangle" size={16} className="text-destructive mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      {t('import.summary.rowField', {
                        row: error?.row,
                        field: formatErrorField(error?.field),
                      })}
                    </p>
                    <p className="text-muted-foreground mt-1">{error?.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {t('import.summary.correctAndRetry')}
          </p>
        </div>
      )}

      {duplicateErrors.length > 0 && (
        <div className="border-t border-border pt-4 mt-4">
          <h4 className="text-sm font-semibold text-foreground mb-2">{t('import.summary.skippedDuplicates')}</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto custom-scrollbar text-xs text-muted-foreground">
            {duplicateErrors.map((error, index) => (
              <p key={index}>{error.message}</p>
            ))}
          </div>
        </div>
      )}

      {/* Success / partial success */}
      {successful > 0 && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Icon name="CheckCircle2" size={24} className="text-success" />
            <div>
              <p className="font-semibold text-success">
                {validationErrors.length === 0 && skippedDuplicates === 0
                  ? t('import.summary.completedSuccess')
                  : t('import.summary.completedPartial')}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {hasBreakdown && (inserted > 0 || updated > 0) ? (
                  <>
                    {inserted > 0 && t('import.summary.addedNew', { count: inserted })}
                    {inserted > 0 && updated > 0 && ', '}
                    {updated > 0 && t('import.summary.updatedExisting', { count: updated })}
                    .
                  </>
                ) : (
                  t('import.summary.candidatesSaved', { count: successful })
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationSummary;
